import React, { useRef } from 'react';
import { AR, Play, ThreeSixtySpin } from '@one-thd/sui-icons';
import { mainImageDefaultProps, mainImagePropTypes } from '../propTypes';
import { HotspotTagIcon } from '../../subcomponents/Icons';
import { ReactImageMagnify } from '../../subcomponents/ReactImageMagnify/ReactImageMagnify';
import Helper from '../../Helper';
import PaintManualImage from './PaintManualImage';
import PaintRGBImage from './PaintRGBImage';
import ImageWithIcon from './ImageWithIcon';
import { getImageMagnifyProps } from '../utils/mediaGalleryUtils';
import { MEDIA_GALLERY_CONTAINER_MAXHEIGHT, MEDIA_GALLERY_HOTSPOTS_MAXWIDTH } from '../constants';

const MainImage = ({
  mainImage,
  selectedThumbnailIndex,
  media,
  identifiers,
  mainImageFetchPriority,
  overrideData,
  showAvataarDefault,
  onMainImageClick
}) => {
  const mainImageRef = useRef();

  const { highResolution = [], main = [], zoom = [] } = media;
  const mainAsset = mainImage || main[selectedThumbnailIndex] || {};
  const {
    id, mediaStill, url, videoStill, type, collection, altText
  } = mainAsset;
  const highResImage = highResolution.find((image) => image.id === id);
  const zoomedAsset = highResImage || zoom.find((z) => z.id === id);
  const largeImage = zoomedAsset ? zoomedAsset?.url : mainAsset?.url;
  const brandNameLabel = overrideData?.brandNameLabel ?? identifiers?.brandName ?? '';
  const galleryLabel = overrideData?.galleryLabel ?? identifiers?.productLabel ?? '';
  const enlargeImage = Helper.isImage({ media: mainAsset });
  const alt = altText || id;
  const smallImageAlt = altText || `${brandNameLabel} ${galleryLabel}`;
  const isConfigurableS3 = mainAsset?.source === 'CONFIGURABLE_S3';

  return (
    mainAsset ? (
      <div
        data-testid="MainImage"
        className="sui-flex sui-flex-col sui-items-center sui-justify-center"
        style={{
          maxHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT,
          minHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT
        }}
        ref={mainImageRef}
      >
        {Helper.isHotspot({ media: mainAsset }) && (
          <div
            className="sui-flex sui-items-center sui-justify-center sui-overflow-hidden"
            style={{ maxHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT, maxWidth: MEDIA_GALLERY_HOTSPOTS_MAXWIDTH }}
          >
            <HotspotTagIcon
              hotspots={mainAsset?.hotspots}
              ocrHotspots={mainAsset?.ocrHotspots}
              openOverlay
              onHotspotImageClick={() => onMainImageClick(type)}
              imageUrl={mainAsset?.url}
              mediaGalleryIndex={selectedThumbnailIndex}
            />
          </div>
        )}
        {!Helper.isHotspot({ media: mainAsset }) && (
          <button
            style={{ maxHeight: MEDIA_GALLERY_CONTAINER_MAXHEIGHT }}
            type="button"
            onClick={() => onMainImageClick(type)}
          >
            {Helper.isVideo({ media: mainAsset }) && (
              <ImageWithIcon
                src={videoStill}
                alt={alt || 'video still'}
                className="sui-w-full sui-h-full sui-object-contain"
              >
                <Play color="current" size="large" />
              </ImageWithIcon>
            )}
            {Helper.isNonAvataarAR({ media: mainAsset }) && (
              <ImageWithIcon
                src={mediaStill}
                alt={alt || 'AR still'}
                className="sui-w-full sui-h-full sui-object-contain"
                style={{ maxWidth: '600px' }}
              >
                <AR color="current" size="large" />
              </ImageWithIcon>
            )}
            {Helper.isAvataarAR({ media: mainAsset }) && (
              <ImageWithIcon
                src={mediaStill}
                alt={alt || '3D still'}
                className="sui-w-full sui-h-full sui-object-contain"
                style={{ maxWidth: '600px' }}
              >
                <AR color="current" size="large" />
              </ImageWithIcon>
            )}
            {Helper.isImage360({ media: mainAsset }) && !showAvataarDefault && (
              <ImageWithIcon
                src={mediaStill}
                alt={alt || '360 image'}
                className="sui-w-full sui-h-full sui-object-contain"
              >
                <ThreeSixtySpin color="current" size="large" />
              </ImageWithIcon>
            )}
            {Helper.isCustomColor({ media: mainAsset }) && <PaintRGBImage asset={mainAsset} />}
            {Helper.isManualColor({ media: mainAsset }) && <PaintManualImage asset={mainAsset} />}
            {enlargeImage && (
              <>
                <ReactImageMagnify {...getImageMagnifyProps({
                  smallImageAlt,
                  url,
                  mainImageFetchPriority,
                  largeImage,
                  highResImage,
                  isConfigurableS3
                })}
                />
                {collection && (
                  <div className="shopThisRoomContainer">
                    <a
                      className="shopThisRoomLink"
                      target="_blank"
                      rel="noreferrer"
                      href={collection}
                      onClick={(e) => e && e.stopPropagation()}
                    >
                      Shop This Room
                    </a>
                  </div>
                )}

              </>
            )}
            {!Helper.isTouchEnabled() && enlargeImage && (
              <span className="sui-flex sui-justify-center">Hover Image to Zoom</span>
            )}
            {!Helper.isTouchEnabled() && !enlargeImage && (
              <span style={{ height: '20px' }} />
            )}
          </button>
        )}
      </div>
    ) : null
  );
};

MainImage.propTypes = mainImagePropTypes;
MainImage.defaultProps = mainImageDefaultProps;

export default MainImage;